import { apiBlob, apiRequest, apiUpload } from './httpClient';

export function uploadDocument(file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiUpload('/api/documents/upload', formData);
}

// Returns a ManualDocumentOut (DocumentOut fields plus title/content/type
// - see backend/schemas/document.py) for a document typed directly
// instead of uploaded.
export function createManualDocument({ title, content }) {
  return apiRequest('/api/documents/manual', {
    method: 'POST',
    body: { title, content },
  });
}

// Replaces a manual document's title and text. Returns a
// ManualDocumentOut; the document goes back to "pending" while the
// backend re-indexes the new text. 400 for an uploaded document, 409
// while it is still being processed.
export function updateManualDocument(documentId, { title, content }) {
  return apiRequest(`/api/documents/${documentId}/manual`, {
    method: 'PUT',
    body: { title, content },
  });
}

// Replaces the full text of an uploaded .txt file (the stored file is
// rewritten and re-indexed). Returns DocumentOut plus `content`; the
// document goes back to "pending" while that happens. 400 for anything
// but a text upload, 409 while it is still being processed.
export function updateTextDocument(documentId, content) {
  return apiRequest(`/api/documents/${documentId}/text`, {
    method: 'PUT',
    body: { content },
  });
}

// Returns DocumentOut[] - newest first (see repositories/
// document_repository.py's list_by_user).
export function getDocuments() {
  return apiRequest('/api/documents');
}

// Returns DocumentOut plus `content`: the extracted text (or the typed
// text, for a manual document). content is null until the backend has
// finished extracting it, or if extraction failed.
export function getDocumentContent(documentId) {
  return apiRequest(`/api/documents/${documentId}/content`);
}

// The original uploaded file, exactly as it was uploaded, as a Blob.
// 404 for a manual document (there is no file).
export function getDocumentFile(documentId) {
  return apiBlob(`/api/documents/${documentId}/file`);
}

// The sheets of an uploaded .xlsx as tables of display text:
// { sheets: [{ name, rows: string[][], truncated }] }. Readable as soon as
// the file is uploaded. 400 for anything but a spreadsheet.
export function getDocumentSheets(documentId) {
  return apiRequest(`/api/documents/${documentId}/sheets`);
}

export async function searchDocuments(query) {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) return [];

  try {
    return await apiRequest(
      `/api/documents/search?q=${encodeURIComponent(trimmedQuery)}`
    );
  } catch (err) {
    if (err.status !== 404) throw err;
  }

  const documents = await getDocuments();
  const normalizedQuery = trimmedQuery.toLowerCase();

  return documents.filter((document) =>
    document.file_name.toLowerCase().includes(normalizedQuery)
  );
}

export function deleteDocument(documentId) {
  return apiRequest(`/api/documents/${documentId}`, { method: 'DELETE' });
}
